/**
 * UserAvatar - Initialen-Kreis als Profilbild-Ersatz
 *
 * Zeigt die Initialen des Benutzernamens in einem farbigen Kreis.
 * Farbe wird deterministisch aus dem Namen abgeleitet.
 */

interface UserAvatarProps {
  name: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const COLORS = [
  'bg-blue-600',
  'bg-emerald-600',
  'bg-amber-600',
  'bg-rose-600',
  'bg-violet-600',
  'bg-cyan-600',
  'bg-orange-600',
  'bg-teal-600',
  'bg-indigo-600',
  'bg-pink-600',
];

const SIZE_CLASSES = {
  sm: 'w-7 h-7 text-xs',
  md: 'w-9 h-9 text-sm',
  lg: 'w-16 h-16 text-xl',
};

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0 || !parts[0]) return '?';
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

function getColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return COLORS[Math.abs(hash) % COLORS.length];
}

export function UserAvatar({ name, size = 'md', className = '' }: UserAvatarProps) {
  const initials = getInitials(name);
  const color = getColor(name);
  const sizeClass = SIZE_CLASSES[size];

  return (
    <div
      className={`${color} ${sizeClass} rounded-full flex items-center justify-center text-white font-semibold select-none shrink-0 ${className}`}
      title={name}
    >
      {initials}
    </div>
  );
}
